import React, { useState } from 'react'
import styled from 'styled-components'
import SvgFragment from '../../../../../fragments/SvgFragment'

interface ITrainAiModalProps {
    onClose: () => void
    onSkip: () => void
    onConfirm: (opts: { monthsBack: number; maxMessages: number }) => void
}

const PERIOD_OPTIONS = [
    { value: 1,  label: '1 month',  note: 'Fastest · Recent context only' },
    { value: 3,  label: '3 months', note: 'Recommended · Good balance', default: true },
    { value: 6,  label: '6 months', note: 'Thorough · More context, slower' },
    { value: 12, label: '1 year',   note: 'Comprehensive · Rich data, significantly slower' },
    { value: 24, label: '2 years',  note: 'Maximum · Deepest analysis, slowest' },
]

const MESSAGE_OPTIONS = [
    { value: 10, label: '10 messages', note: 'Fast · Captures the essence' },
    { value: 20, label: '20 messages', note: 'Balanced · More context' },
    { value: 30, label: '30 messages', note: 'Detailed · Full depth, slowest', default: true },
]

const TrainAiModal: React.FC<ITrainAiModalProps> = ({ onClose, onSkip, onConfirm }) => {
    const [monthsBack, setMonthsBack] = useState(3)
    const [maxMessages, setMaxMessages] = useState(30)

    return (
        <Overlay onClick={onClose}>
            <Modal onClick={e => e.stopPropagation()}>
                <Header>
                    <AiIconWrap>
                        <SvgFragment type="aiAssistant" />
                    </AiIconWrap>
                    <Title>Train AI with Your Conversations</Title>
                </Header>

                <SecurityBanner>
                    <ShieldIcon>🔒</ShieldIcon>
                    <SecurityText>
                        <strong>Your conversations are safe.</strong> LeadDelta stores your LinkedIn conversations securely. They are used exclusively to personalise your AI assistant. You can request deletion at any time.
                    </SecurityText>
                </SecurityBanner>

                <TwoColumns>
                    <Section>
                        <SectionLabel>How far back to go</SectionLabel>
                        <SectionHint>More history = better AI, but slower loading time.</SectionHint>
                        <OptionList>
                            {PERIOD_OPTIONS.map(opt => (
                                <OptionRow key={opt.value} $selected={monthsBack === opt.value} onClick={() => setMonthsBack(opt.value)}>
                                    <Radio $selected={monthsBack === opt.value} />
                                    <OptionBody>
                                        <OptionLabel>
                                            {opt.label}
                                            {opt.default && <DefaultBadge>Default</DefaultBadge>}
                                        </OptionLabel>
                                        <OptionNote>{opt.note}</OptionNote>
                                    </OptionBody>
                                </OptionRow>
                            ))}
                        </OptionList>
                    </Section>

                    <Divider />

                    <Section>
                        <SectionLabel>Messages per conversation</SectionLabel>
                        <SectionHint>More messages = richer context, slower loading time.</SectionHint>
                        <OptionList>
                            {MESSAGE_OPTIONS.map(opt => (
                                <OptionRow key={opt.value} $selected={maxMessages === opt.value} onClick={() => setMaxMessages(opt.value)}>
                                    <Radio $selected={maxMessages === opt.value} />
                                    <OptionBody>
                                        <OptionLabel>
                                            {opt.label}
                                            {opt.default && <DefaultBadge>Default</DefaultBadge>}
                                        </OptionLabel>
                                        <OptionNote>{opt.note}</OptionNote>
                                    </OptionBody>
                                </OptionRow>
                            ))}
                        </OptionList>
                    </Section>
                </TwoColumns>

                <Actions>
                    <ActionsLeft>
                        <SkipBtn type="button" onClick={onSkip}>Skip</SkipBtn>
                        <CancelBtn type="button" onClick={onClose}>Cancel</CancelBtn>
                    </ActionsLeft>
                    <ConfirmBtn type="button" onClick={() => onConfirm({ monthsBack, maxMessages })}>
                        Start Training
                    </ConfirmBtn>
                </Actions>
            </Modal>
        </Overlay>
    )
}

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
`
const Modal = styled.div`
    background: white;
    border-radius: 14px;
    padding: 28px 32px 24px;
    width: 680px;
    max-width: 96vw;
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
`
const Header = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
`
const AiIconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    background: #eef3ff;
    flex-shrink: 0;

    svg {
        width: 20px;
        height: 20px;
    }
    svg path, svg [fill] {
        fill: #5964ff;
        stroke: #5964ff;
    }
`
const Title = styled.h3`
    font-size: 17px;
    font-weight: 700;
    color: #1e2230;
    margin: 0;
`
const SecurityBanner = styled.div`
    display: flex;
    gap: 12px;
    background: #f0f6ff;
    border: 1px solid #c8d8f8;
    border-radius: 10px;
    padding: 14px 16px;
    margin-bottom: 22px;
`
const ShieldIcon = styled.div`
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 1px;
`
const SecurityText = styled.p`
    font-size: 13px;
    color: #3a4460;
    line-height: 1.55;
    margin: 0;
`
const TwoColumns = styled.div`
    display: flex;
    gap: 0;
    align-items: flex-start;
    margin-bottom: 20px;
`
const Divider = styled.div`
    width: 1px;
    align-self: stretch;
    background: #f0f2f5;
    margin: 0 20px;
    flex-shrink: 0;
`
const Section = styled.div`
    flex: 1;
    min-width: 0;
`
const SectionLabel = styled.div`
    font-size: 13px;
    font-weight: 700;
    color: #1e2230;
    margin-bottom: 3px;
`
const SectionHint = styled.div`
    font-size: 12px;
    color: #8c96ad;
    margin-bottom: 10px;
`
const OptionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`
const OptionRow = styled.div<{ $selected: boolean }>`
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 9px 12px;
    border-radius: 8px;
    border: 1.5px solid ${props => props.$selected ? '#5964ff' : '#e5e9ef'};
    background: ${props => props.$selected ? '#f5f7ff' : '#fff'};
    cursor: pointer;
    transition: border-color 0.12s, background 0.12s;
    &:hover {
        border-color: #5964ff;
        background: #f5f7ff;
    }
`
const Radio = styled.div<{ $selected: boolean }>`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${props => props.$selected ? '#5964ff' : '#c5cdd8'};
    background: ${props => props.$selected ? '#5964ff' : '#fff'};
    flex-shrink: 0;
    margin-top: 2px;
    position: relative;
    &::after {
        content: '';
        display: ${props => props.$selected ? 'block' : 'none'};
        position: absolute;
        top: 3px;
        left: 3px;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: white;
    }
`
const OptionBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`
const OptionLabel = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #1e2230;
    display: flex;
    align-items: center;
    gap: 6px;
`
const DefaultBadge = styled.span`
    font-size: 10px;
    font-weight: 700;
    color: #5964ff;
    background: #eef3ff;
    border-radius: 4px;
    padding: 1px 6px;
    letter-spacing: 0.3px;
    text-transform: uppercase;
`
const OptionNote = styled.div`
    font-size: 12px;
    color: #8c96ad;
`
const Actions = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding-top: 16px;
    border-top: 1px solid #f0f2f5;
`
const ActionsLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`
const SkipBtn = styled.button`
    padding: 8px 20px;
    border-radius: 8px;
    border: 1px solid #dae0e8;
    background: white;
    font-size: 14px;
    cursor: pointer;
    color: #545e77;
    &:hover { background: #f6f8fb; }
`
const CancelBtn = styled.button`
    padding: 8px 20px;
    border-radius: 8px;
    border: 1px solid #dae0e8;
    background: white;
    font-size: 14px;
    cursor: pointer;
    color: #545e77;
    &:hover { background: #f6f8fb; }
`
const ConfirmBtn = styled.button`
    padding: 8px 22px;
    border-radius: 8px;
    border: none;
    background: #5964ff;
    color: white;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    &:hover { background: #4a53e0; }
`

export default TrainAiModal
